import { Type } from '@angular/core';
import { PrintMenu } from '../print-menu';
import { ProductMenuHeaderComponent } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-header/product-menu-header.component';
import { MenuHeaderTitleImageComponent } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-header/menu-header-title/menu-header-title-image/menu-header-title-image.component';
import { ColumnGrouping } from '../section/column-grouping';
import { Section } from '../section/section';
import { SectionRowViewModel } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-section/product-section/section-row-view-models/SectionRowViewModel';
import { BandedRowMode } from '../../enum/shared/banded-row-mode.enum';
import { BandedRowColorContrast } from '../../enum/shared/banded-row-color-contrast.enum';
import { ColWidth } from '../../shared/col-width';
import { exists } from '../../../functions/exists';
import { ColorUtils } from '../../../utils/color-utils';
import { PrintHeaderLayoutType } from '../../enum/shared/print-header-layout-type.enum';
import { ColumnUtils } from '../../../utils/column-utils';
import { FONT, FontStyleSheet } from '../font-style-sheets';
import { SectionColumnType } from '../../enum/shared/section-column-type';
import { SectionColumnProductInfoType } from '../../enum/shared/section-column-product-info-type';
import { SectionColumnViewModel } from 'src/app/modules/display/components/menus/product-menu/building-blocks/menu-section/product-section/section-column-view-models/SectionColumnViewModel';
import type { WithinNameCell } from '../../enum/shared/within-name-cell';

export class FireAndFlowerPrintMenu extends PrintMenu {
  getPageTopMargin(): string {
    return '0.5in';
  }

  getPageBottomMargin(): string {
    return '0.5in';
  }

  getPageLeftMargin(): string {
    return '0.5in';
  }

  getPageRightMargin(): string {
    return '0.5in';
  }

  getThemeClass(): string {
    return 'fire-and-flower-print-theme';
  }

  override getHeaderType(): Type<ProductMenuHeaderComponent> {
    return MenuHeaderTitleImageComponent;
  }

  override getPrintHeaderLayoutType(): PrintHeaderLayoutType {
    return this.menuOptions?.printHeaderLayout || PrintHeaderLayoutType.FirstPage;
  }

  override whatFontsAreNeededToRenderContent(): FontStyleSheet[] {
    return [
      FONT.OpenSans.SemiBold_600,
      FONT.FuturaPt.Regular_400,
      FONT.FuturaPt.Medium_500,
      FONT.FuturaPt.SemiBold_600,
      FONT.FuturaPt.Bold_700
    ];
  }

  override getAdobeFontFamilyCssUrl(): string | null {
    return 'https://use.typekit.net/tof6ywr.css';
  }

  override getColumnOrdering(): Map<SectionColumnType, number> {
    return new Map<SectionColumnType, number>([
      [SectionColumnProductInfoType.ProductTitle, 0],
      [SectionColumnProductInfoType.Label, 1],
      [SectionColumnProductInfoType.Badge, 2],
      [SectionColumnProductInfoType.Strain, 3],
      [SectionColumnProductInfoType.StrainType, 4],
      [SectionColumnProductInfoType.Brand, 5],
      ...SectionColumnViewModel.getDefaultAdditionalDetailsColumnOrder(6),
      SectionColumnViewModel.getDefaultProductTagGroupColumnOrdering(7),
      ...SectionColumnViewModel.getDefaultCannabinoidColumnOrdering(8),
      ...SectionColumnViewModel.getDefaultTerpeneColumnOrdering(9),
      [SectionColumnProductInfoType.QuantityAndSize, 10],
      [SectionColumnProductInfoType.Quantity, 11],
      [SectionColumnProductInfoType.Size, 12],
      [SectionColumnProductInfoType.VariantType, 13],
      [SectionColumnProductInfoType.Stock, 14],
      [SectionColumnProductInfoType.GradingResults, 15],
      [SectionColumnProductInfoType.VariantPrice, 16],
      [SectionColumnProductInfoType.VariantSecondaryPrice, 17]
    ]);
  }

  override getShouldInflateColumnHeaderForRow(
    s: Section,
    col: SectionColumnViewModel,
    rows: SectionRowViewModel[]
  ): boolean {
    const badgeCol = col?.columnType === SectionColumnProductInfoType.Badge;
    const labelCol = col?.columnType === SectionColumnProductInfoType.Label;
    if (badgeCol || labelCol) {
      return exists(col?.columnTitle);
    }
    return super.getShouldInflateColumnHeaderForRow(s, col, rows);
  }

  override getShouldInflateColumnForRow(s: Section, col: SectionColumnViewModel, row: SectionRowViewModel): boolean {
    const badge = col?.columnType === SectionColumnProductInfoType.Badge;
    return !badge || (badge && exists(row?.hasBadges()));
  }

  override hideProductLabelColumnWhenEmpty(): boolean {
    return true;
  }

  override ignoreLabelColumnWidth(): boolean {
    return true;
  }

  override getThemeSpecifiedColumnViewModels(
    sectionRowViewModels: SectionRowViewModel[],
    sectionRowViewModel: SectionRowViewModel,
    widths: ColWidth[] = this.getColWidths(sectionRowViewModel)
  ): SectionColumnViewModel[] {
    const vms = super.getThemeSpecifiedColumnViewModels(sectionRowViewModels, sectionRowViewModel, widths);
    if (!sectionRowViewModel.hideLabel) {
      const layoutType = sectionRowViewModel?.getLayoutType();
      const labelCol = new SectionColumnViewModel(layoutType, null);
      labelCol.columnType = SectionColumnProductInfoType.Label;
      labelCol.columnTitle = '';
      labelCol.widthPercentage = '15';
      vms.splice(1, 0, labelCol);
    }
    return vms;
  }

  override getShowInlineLabels(): WithinNameCell | null {
    return null;
  }

  override bandedRowsDefaultEnabled(): boolean {
    return true;
  }

  override bandedRowMode(): BandedRowMode {
    return BandedRowMode.Odd;
  }

  override bandedRowColorContrast(): BandedRowColorContrast {
    return BandedRowColorContrast.Lighten;
  }

  override getSectionHeaderUnderlined(): boolean {
    return false;
  }

  override getSectionTitleUnderlineColor(section: Section): string {
    const headerTextColor = section?.getSectionHeaderTitleColor();
    return exists(headerTextColor) ? headerTextColor : super.getSectionTitleUnderlineColor(section);
  }

  override getSectionTitleBorderColor(section: Section): string {
    const headerTextColor = section?.getSectionHeaderTitleColor();
    return exists(headerTextColor) ? headerTextColor : super.getSectionTitleBorderColor(section);
  }

  override getLineModeColumnGroupings(sectionColumnViewModels: SectionColumnViewModel[]): ColumnGrouping[] {
    return ColumnUtils.oneBigGrouping();
  }

  override getGridModeColumnGroupings(sectionColumnViewModels: SectionColumnViewModel[]): ColumnGrouping[] {
    return ColumnUtils.oneBigGrouping();
  }

  override getDefaultBandingHexString(): [number, number, number] {
    return ColorUtils.hexToRgb('#d2d2d2');
  }

  override getDefaultBandingOpacity(): number {
    return 1;
  }

  override getTitleSectionTopMargin(): string {
    const topMargin = (16 / 11) * 1.5;
    return `${topMargin}rem`;
  }
}
